import { z } from "zod";
import { IntIdSchema, YYYYMMDDSchema, YYYYMMDDTimeSchema, UUIDSchema } from "../common/dto";
import { NullableCourseCategorySchema, NullableBufferZoneSchema } from "../course/dto";
import { AssignedLimitationSchema } from "../limitations/dto";

// =============  Relation Tree Base Schemas =============
export const NodeSchema = z.object({
  id: IntIdSchema,
  name: z.string(),
});

export const ComplianceStatusSchema = z.enum(["ok", "never_expires", "buffer", "expired", "missing"]);
export type ComplianceStatus = z.infer<typeof ComplianceStatusSchema>;

export const CourseTypeSchema = z.enum(["moodle", "training", "custom", "company"]);
export type CourseType = z.infer<typeof CourseTypeSchema>;

/**
 * Status schema with compliance status and validity date
 *
 * - status: null means no courses assigned at this level
 * - validUntil: null means no status OR never expires
 */
export const StatusSchema = z.object({
  status: ComplianceStatusSchema.nullable(),
  validUntil: YYYYMMDDSchema.nullable(),
});

/**
 * Self status schema - for nodes that have both aggregated and self-only status
 * Used by: Operations (non-aircraft), Aircraft (operation-level)
 *
 * - status/validUntil: Aggregated with parent
 * - operationStatus/operationValidUntil: Operation-level courses only (context-aware: non-aircraft for operations, aircraft for aircraft nodes)
 */
export const SelfStatusSchema = StatusSchema.extend({
  operationStatus: ComplianceStatusSchema.nullable(),
  operationValidUntil: YYYYMMDDSchema.nullable(),
});

export const CourseIdSchema = z.string();

export type Node = z.infer<typeof NodeSchema>;
export type Status = z.infer<typeof StatusSchema>;
export type CourseId = z.infer<typeof CourseIdSchema>;

// ============= Training Hierarchy Schema =============
export const TrainingHierarchySchema = z.object({
  highestInTrainingHierarchyCourseId: z.number().nullable(),
  highestInTrainingHierarchyCourseName: z.string().nullable(),
  passedByCourseId: z.number().nullable(),
  passedByCourseName: z.string().nullable(),
  passedByTrainingId: z.number().nullable(),
});

export type TrainingHierarchy = z.infer<typeof TrainingHierarchySchema>;

// ============= Course Certificate Schema =============
export const CertificateSchema = z.object({
  id: IntIdSchema.nullable(),
  approvedAt: YYYYMMDDSchema.nullable().optional(),
  approvedBy: z.string().nullable().optional(),
  certificateFile: z.string().nullable().optional(),
  completedDate: YYYYMMDDTimeSchema.nullable(),
  crewName: z.string().nullable().optional(),
  isPartOfTraining: z.boolean().nullable().optional(),
  status: ComplianceStatusSchema,
  uploadedAt: YYYYMMDDSchema.nullable().optional(),
  uploadId: IntIdSchema.nullable().optional(),
  validUntil: YYYYMMDDSchema.nullable(),
});

export const CourseCertificateSchema = z.object({
  id: IntIdSchema,
  name: z.string(),
  price: z.number().nullable().optional(),
  // Effective default validity length in months for this user. When the user's position has a
  // position-specific override, this is the override (so the value matches the certificate-upload
  // form). 0 / null means "never expires".
  validPeriod: z.number().nullable().optional(),
  // The course's own default validity in months. When it differs from `validPeriod`, the latter is
  // a position-specific override and clients can show a hint (e.g. "12 months (overrides course
  // default: 6 months)").
  courseDefaultValidPeriod: z.number().nullable().optional(),
  bufferZone: NullableBufferZoneSchema,
  inactive: z.boolean().nullable(),
  type: CourseTypeSchema.optional(),
  courseCategory: NullableCourseCategorySchema,
  certificate: CertificateSchema,
  trainingHierarchy: TrainingHierarchySchema.optional(),
});

const ActionCourseSchema = CourseCertificateSchema.omit({
  certificate: true,
}).nullable();

export const CourseCertificateWithRefreshersSchema = CourseCertificateSchema.extend({
  refreshers: z.array(z.number()).optional(),
  group: z
    .object({
      id: z.number(),
      name: z.string(),
      courses: z.array(z.number()),
    })
    .optional(),
  custom: z
    .object({
      history: z.array(CertificateSchema),
    })
    .optional(),
});

export const CertificateActionsSchema = z.object({
  canUploadCertificate: z.boolean(),
  downloadableCertificate: CertificateSchema.nullable(),
  hasHistory: z.boolean(),
  isPendingApproval: z.boolean(),
  isPurchased: z.boolean(),
  isReupload: z.boolean(),
  noNewCourseAvailable: z.boolean(),
  playableCourse: ActionCourseSchema,
  purchasableCourse: ActionCourseSchema,
});

export type Certificate = z.infer<typeof CertificateSchema>;
export type CertificateActions = z.infer<typeof CertificateActionsSchema>;
export type CourseCertificate = z.infer<typeof CourseCertificateSchema>;
export type CourseCertificateWithRefreshers = z.infer<typeof CourseCertificateWithRefreshersSchema>;
export type ActionCourse = z.infer<typeof ActionCourseSchema>;
export const CourseCertificateWithActionsSchema = CourseCertificateWithRefreshersSchema.extend({
  actions: CertificateActionsSchema,
});

export type CourseCertificateWithActions = z.infer<typeof CourseCertificateWithActionsSchema>;

// Additional useful type exports
export type CertificateMap = Record<string | number, CourseCertificateWithActions>;

// ============= Response Schemas =============
export const ComplianceResponseSchema = z.object({
  roles: z.array(
    NodeSchema.extend({
      positions: z.array(
        NodeSchema.extend(StatusSchema.shape).extend({
          aircraft: z.array(NodeSchema.extend(StatusSchema.shape)).optional(),
          operations: z.array(
            NodeSchema.extend(SelfStatusSchema.shape).extend({
              aircraft: z.array(NodeSchema.extend(SelfStatusSchema.shape)).optional(),
            }),
          ),
        }),
      ),
    }),
  ),
});

export const TreeWithCoursesSchema = z.object({
  roles: z.array(
    NodeSchema.extend(StatusSchema.shape).extend({
      courseIds: z.array(CourseIdSchema),
      aircraft: z
        .array(
          NodeSchema.extend(StatusSchema.shape).extend({
            courseIds: z.array(CourseIdSchema),
          }),
        )
        .optional(),
      positions: z.array(
        NodeSchema.extend(StatusSchema.shape).extend({
          courseIds: z.array(CourseIdSchema),
          aircraft: z
            .array(
              NodeSchema.extend(StatusSchema.shape).extend({
                courseIds: z.array(CourseIdSchema),
              }),
            )
            .optional(),
          operations: z.array(
            NodeSchema.extend(SelfStatusSchema.shape).extend({
              courseIds: z.array(CourseIdSchema),
              aircraft: z
                .array(
                  NodeSchema.extend(SelfStatusSchema.shape).extend({
                    courseIds: z.array(CourseIdSchema),
                  }),
                )
                .optional(),
            }),
          ),
        }),
      ),
    }),
  ),
});

export const ComplianceWithCoursesResponseSchema = z.object({
  certificateMap: z.record(z.string(), CourseCertificateWithActionsSchema.partial()),
  relationTree: TreeWithCoursesSchema,
});

const LocalEntityRefSchema = z.object({
  id: IntIdSchema,
  name: z.string(),
});

const AircraftOverviewStatusSchema = z.object({
  status: ComplianceStatusSchema.nullable(),
  validUntil: YYYYMMDDSchema.nullable(),
});

export const AircraftOverviewColumnSchema = z.object({
  key: z.string(),
  roleId: IntIdSchema,
  roleName: z.string(),
  positionId: IntIdSchema,
  positionName: z.string(),
  operationId: IntIdSchema,
  operationName: z.string(),
});

export const AircraftOperationComplianceSchema = z.object({
  operation: AircraftOverviewStatusSchema,
  aircraft: AircraftOverviewStatusSchema,
  combined: AircraftOverviewStatusSchema,
  underSupervision: z.boolean(),
  limitations: z.array(AssignedLimitationSchema).default([]),
  canPerform: z.boolean(),
});

export const AircraftOverviewRowSchema = z.object({
  key: z.string(),
  userId: IntIdSchema,
  userName: z.string(),
  aircraftId: IntIdSchema,
  aircraftName: z.string(),
  operationCompliance: z.record(z.string(), AircraftOperationComplianceSchema),
});

export const AircraftOverviewRowCacheSchema = AircraftOverviewRowSchema.extend({
  baseIds: z.array(IntIdSchema).default([]),
});

export const AircraftOverviewReportRecordSchema = z.object({
  user: LocalEntityRefSchema,
  aircraft: LocalEntityRefSchema,
  role: LocalEntityRefSchema,
  position: LocalEntityRefSchema,
  operation: LocalEntityRefSchema,
  operationStatus: AircraftOverviewStatusSchema,
  aircraftStatus: AircraftOverviewStatusSchema,
  combinedStatus: AircraftOverviewStatusSchema,
  underSupervision: z.boolean(),
  limitations: z.array(AssignedLimitationSchema).default([]),
  canPerform: z.boolean(),
});

export const AircraftOverviewResponseSchema = z.object({
  crew: LocalEntityRefSchema,
  columns: z.array(AircraftOverviewColumnSchema),
  rows: z.array(AircraftOverviewRowSchema),
});

export const AircraftOverviewCacheArtifactSchema = z.object({
  crew: LocalEntityRefSchema,
  columns: z.array(AircraftOverviewColumnSchema),
  rows: z.array(AircraftOverviewRowCacheSchema),
  records: z.array(AircraftOverviewReportRecordSchema),
  indexes: z.object({
    report: z.object({
      byCrewId: z.record(z.string(), z.array(AircraftOverviewReportRecordSchema)),
    }),
    externalOperation: z.object({
      byCrewId: z.record(z.string(), z.record(UUIDSchema, z.array(AircraftOverviewReportRecordSchema))),
      skipped: z.object({
        missingOperationExternalId: z.number().int().min(0),
      }),
    }),
    userCalculatedAt: z.record(z.string(), z.iso.datetime()),
  }),
});

export type TreeWithCourses = z.infer<typeof TreeWithCoursesSchema>;
export type ComplianceResponse = z.infer<typeof ComplianceResponseSchema>;
export type ComplianceWithCoursesResponse = z.infer<typeof ComplianceWithCoursesResponseSchema>;
export type AircraftOverviewColumn = z.infer<typeof AircraftOverviewColumnSchema>;
export type AircraftOperationCompliance = z.infer<typeof AircraftOperationComplianceSchema>;
export type AircraftOverviewRow = z.infer<typeof AircraftOverviewRowSchema>;
export type AircraftOverviewRowCache = z.infer<typeof AircraftOverviewRowCacheSchema>;
export type AircraftOverviewReportRecord = z.infer<typeof AircraftOverviewReportRecordSchema>;
export type AircraftOverviewResponse = z.infer<typeof AircraftOverviewResponseSchema>;
export type AircraftOverviewCacheArtifact = z.infer<typeof AircraftOverviewCacheArtifactSchema>;

// Helper types for easier access
export type RoleNode = TreeWithCourses["roles"][number];
export type PositionNode = RoleNode["positions"][number];
export type OperationNode = PositionNode["operations"][number];
export type AircraftNode = NonNullable<OperationNode["aircraft"]>[number];

// ============= Batch Response Schemas =============
export const ComplianceWithCoursesBatchResponseSchema = z.record(z.string(), ComplianceWithCoursesResponseSchema);
export type ComplianceWithCoursesBatchResponse = z.infer<typeof ComplianceWithCoursesBatchResponseSchema>;

// ============= Upload Body Schemas =============
// IDs arrive as multipart form strings, so we coerce to number
const IdSchema = z.coerce.number().int().positive();

export const UploadCertSchema = z.object({
  crewId: IdSchema,
  courseId: IdSchema,
  completed: YYYYMMDDSchema,
  validUntil: YYYYMMDDSchema.nullable().optional(),
  comment: z.string().optional(),
});

export const UserUploadCertSchema = UploadCertSchema.extend({
  userId: IdSchema,
});

// express-fileupload parses repeated form fields into an array when multiple values are
// submitted, but collapses to a plain string when only one value is present.
// The union handles both cases: an array of coerced IDs, or a single coerced ID wrapped in [v].
export const UsersUploadCertSchema = UploadCertSchema.extend({
  userIds: z.union([z.array(IdSchema).min(1), IdSchema.transform((v) => [v])]),
});

export const UserCoursesUploadCertSchema = UploadCertSchema.omit({ courseId: true }).extend({
  userId: IdSchema,
  courseIds: z.union([z.array(IdSchema).min(1), IdSchema.transform((v) => [v])]),
});

export const BulkUploadCertificateResponseSchema = z.object({
  status: z.enum(["ok", "fail"]),
  reason: z.string().optional(),
  created: z.number().int().nonnegative().optional(),
});

/** Longest decline reason a reviewer may enter (CREW-577). */
export const DECLINE_REASON_MAX_LENGTH = 1000;

/**
 * Body of `PUT /certificate/pending/:crewId/resolve/:id`. Declining needs a reason (CREW-577): it is
 * stored on the certificate and sent to the person who uploaded it.
 */
export const ResolvePendingCertificateBodySchema = z
  .object({
    isCustom: z.boolean(),
    status: z.enum(["approve", "decline"]),
    declineReason: z.string().trim().min(3).max(DECLINE_REASON_MAX_LENGTH).optional(),
  })
  .refine((body) => body.status !== "decline" || Boolean(body.declineReason), {
    message: "A reason is required to decline a certificate",
    path: ["declineReason"],
  });

export type UploadCert = z.infer<typeof UploadCertSchema>;
export type UserUploadCert = z.infer<typeof UserUploadCertSchema>;
export type UsersUploadCert = z.infer<typeof UsersUploadCertSchema>;
export type UserCoursesUploadCert = z.infer<typeof UserCoursesUploadCertSchema>;
export type BulkUploadCertificateResponse = z.infer<typeof BulkUploadCertificateResponseSchema>;
export type ResolvePendingCertificateBody = z.infer<typeof ResolvePendingCertificateBodySchema>;
